'use client'

import { type ReactNode, useCallback, useRef, useState } from 'react'
import { useTranslations } from 'next-intl'
import { isNumber, isString } from 'lodash'
import { cn, isImageURL } from '@/lib/utils'
import {
  useUploadAssets,
  ALLOWED_UPLOAD_ASSETS_FORMATS,
} from '@/hooks/use-upload-assets'
import { UploadIcon, Loader2Icon, XIcon } from 'lucide-react'
import { toast } from 'sonner'
import Image from 'next/image'
import { Button } from '@/components/ui/button'
import { PDF_FILE_THUMBNAIL_URL } from '@/lib/constant'

export const getFileType = (url: string) => {
  const extension = url.split('.').pop()?.toLowerCase()
  if (['jpg', 'jpeg', 'png', 'webp', 'gif', 'svg'].includes(extension || '')) {
    return 'image'
  }
  if (['mp4', 'webm'].includes(extension || '')) {
    return 'video'
  }
  if (extension === 'pdf') {
    return 'pdf'
  }
  if (isImageURL(url)) {
    return 'imageUrl'
  }
  return 'unknown'
}

type FileType = string | File

interface FileUploadProps {
  value?: FileType
  onChange?: (value: string) => void
  assetDir: string
  className?: string
  label?: string
  disabled?: boolean
  accept?: string[]
  autoUpload?: boolean
  beforeUpload?: (files: File[]) => Promise<boolean>
  maxFiles?: number
  previewRender?: (file: FileType) => ReactNode
}

export function FileUpload(props: FileUploadProps) {
  const {
    value,
    onChange,
    assetDir,
    className,
    label,
    disabled = false,
    accept = ALLOWED_UPLOAD_ASSETS_FORMATS,
    autoUpload = true,
    beforeUpload,
    maxFiles,
    previewRender,
  } = props
  const t = useTranslations('ui')

  const inputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const { uploadAssets, uploadProgress } = useUploadAssets()
  const id = useRef(
    `file-upload-${Math.random().toString(36).slice(2)}`,
  ).current

  const validateFiles = async (files: File[]): Promise<boolean> => {
    // Check file count
    if (isNumber(maxFiles) && files.length > maxFiles) {
      toast.error(t('tooManyFiles', { max: maxFiles }))
      return false
    }

    // Check file types
    const invalidFiles = files.filter((file) => {
      const fileType = file.type
      const fileExtension = `.${file.name.split('.').pop()}`
      return !accept.some(
        (format) => format.includes(fileType) || format.includes(fileExtension),
      )
    })

    if (invalidFiles.length > 0) {
      toast.error(
        t('invalidFileType', {
          formats: accept.map((ext) => ext.replace('.', '')).join(', '),
        }),
      )
      return false
    }

    // Run custom validation if provided
    if (beforeUpload) {
      try {
        const isValid = await beforeUpload(files)
        if (!isValid) {
          return false
        }
      } catch (error) {
        console.error('Validation error:', error)
        toast.error(t('validationError'))
        return false
      }
    }

    return true
  }

  const uploadFiles = async (files: File[]) => {
    setIsUploading(true)
    try {
      const result = await uploadAssets(files, {
        assetSource: 'upload',
        assetDir,
      })
      if (!result.success) {
        toast.error(result.error, { duration: 2000 })
        return
      }
      const [asset] = result.assets
      onChange?.(asset.link)
      toast.success(t('uploadSuccess'))
    } catch (error) {
      toast.error(t('uploadError'))
    } finally {
      setIsUploading(false)
    }
  }

  const handleFileChange = async (files: File[]) => {
    if (files.length === 0) return

    const isValid = await validateFiles(files)
    if (!isValid) return

    if (autoUpload) {
      await uploadFiles(files)
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const files = Array.from(e.dataTransfer.files)
    handleFileChange(files)
  }

  const renderPreview = useCallback(() => {
    if (!value || !isString(value)) return null

    const fileType = getFileType(value)

    switch (fileType) {
      case 'image': {
        // Check if the value is a relative path (doesn't start with http/https or /)
        const isRelativePath =
          !value.startsWith('http') &&
          !value.startsWith('https') &&
          !value.startsWith('/')

        if (isRelativePath) {
          // For relative paths, use regular img tag instead of Next.js Image
          return (
            <div className="relative w-32 h-32 rounded-lg overflow-hidden border-[rgba(255,255,255,0.06)] focus-within:border-[rgba(255,255,255,0.2)]">
              <img
                src={value}
                alt="Preview"
                className="object-cover w-full h-full"
              />
            </div>
          )
        } // For absolute URLs or paths starting with /, use Next.js Image
        return (
          <div className="relative w-32 h-32 rounded-lg overflow-hidden border-[rgba(255,255,255,0.06)] focus-within:border-[rgba(255,255,255,0.2)]">
            <Image src={value} alt="Preview" fill className="object-cover" />
          </div>
        )
      }
      case 'imageUrl':
        return (
          <div className="relative w-32 h-32 rounded-lg overflow-hidden">
            <img src={value} alt="Preview" className="object-cover" />
          </div>
        )
      case 'video':
        return (
          <div className="relative w-32 h-32 rounded-lg overflow-hidden">
            <video
              src={value}
              controls
              className="w-full h-full object-cover"
            />
          </div>
        )
      case 'pdf':
        return (
          <div className="relative w-32 h-32 rounded-lg overflow-hidden bg-gray-100">
            <Image
              src={PDF_FILE_THUMBNAIL_URL}
              alt="PDF Preview"
              fill
              className="object-contain p-4"
            />
          </div>
        )
      default:
        return null
    }
  }, [value])

  const defaultPreviewRender = useCallback(
    (value: FileType) => {
      if (!isString(value)) return null
      return (
        <>
          {renderPreview()}
          <div className="flex-1 min-w-0">
            <div className="text-sm text-gray-500 break-words">{value}</div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onChange?.('')}
              disabled={isUploading || disabled}
              className="mt-1"
            >
              <XIcon className="h-4 w-4 mr-1" />
              {t('remove')}
            </Button>
          </div>
        </>
      )
    },
    [disabled, isUploading, renderPreview, t, onChange],
  )

  return (
    <div className="space-y-2">
      {label && (
        <label htmlFor={id} className="text-sm font-medium">
          {label}
        </label>
      )}
      <div className="space-y-2">
        <input
          id={id}
          ref={inputRef}
          type="file"
          accept={accept.join(',')}
          className="hidden"
          onChange={(e) => {
            const files = Array.from(e.target.files || [])
            handleFileChange(files)
          }}
          disabled={isUploading || disabled}
        />
        {value ? (
          <div className="flex items-center gap-4">
            {previewRender ? previewRender(value) : defaultPreviewRender(value)}
          </div>
        ) : (
          <button
            type="button"
            className={cn(
              'w-full border-1 border-dashed bg-accent rounded-lg p-4 text-center transition-colors relative',
              isDragging
                ? 'border-brand bg-brand/5'
                : isUploading
                  ? 'border-brand bg-brand/5'
                  : 'border-[rgba(255,255,255,0.06)] hover:border-[rgba(255,255,255,0.2)]',
              disabled && 'opacity-50 cursor-not-allowed',
              className,
            )}
            onClick={(e: React.MouseEvent) => {
              e.preventDefault()
              inputRef.current?.click()
            }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            disabled={isUploading || disabled}
          >
            {isUploading ? (
              <div className="flex flex-col items-center">
                <div className="p-2 rounded-full bg-gray-100 mb-1">
                  <Loader2Icon className="w-5 h-5 text-gray-500 animate-spin" />
                </div>
                <div className="mb-2">
                  <p className="text-sm font-medium">{t('uploading')}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {Math.round(uploadProgress)}% {t('complete')}
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <UploadIcon className="w-6 h-6 text-gray-400" />
                <p className="text-sm font-medium">
                  {isDragging ? t('drop_file') : t('click_to_upload_file')}
                </p>
                <p className="text-xs text-[rgba(255,255,255,0.6)]">
                  {t('support_ext')}:{' '}
                  {accept.map((ext) => ext.replace('.', '')).join(', ')}
                </p>
              </div>
            )}
          </button>
        )}
      </div>
    </div>
  )
}
